import { useEffect, useMemo, useState } from "react";
import { apiGet, apiPost } from "./api";
import "./viqai-dark.css";

const eur = v => v == null || v === "" ? "" : Number(v).toLocaleString("fr-FR");

export default function NetworkPublishModal({ onClose, initialDossierId = null }) {
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(initialDossierId ? String(initialDossierId) : "");
  const [askingPrice, setAskingPrice] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    let alive = true;
    apiGet("/api/dossiers").then(r => {
      if (!alive) return;
      const rows = r?.success ? (r.dossiers || []).filter(d => !d.archived_at && !d.archived) : [];
      setDossiers(rows);
      setSelectedId(prev => prev || (rows[0]?.id ? String(rows[0].id) : ""));
    }).catch(e => { if (alive) setMessage(e?.message || "Impossible de charger vos Dossiers VIQ."); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const selected = useMemo(() => dossiers.find(d => String(d.id) === String(selectedId)) || null, [dossiers, selectedId]);
  useEffect(() => {
    if (!selected) return;
    const suggested = selected.latest_estimation?.prix_viq ?? selected.latest_estimation?.argus_mid ?? selected.latest_estimation?.price ?? null;
    if (suggested && !askingPrice) setAskingPrice(String(Math.round(Number(suggested))));
  // only seed price when dossier changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const publish = async () => {
    if (!selectedId || busy) return;
    setBusy(true); setMessage("");
    try {
      const r = await apiPost("/api/marketplace/publish", {
        dossier_id: Number(selectedId),
        asking_price: askingPrice ? Number(askingPrice) : null,
        note: note.trim() || null,
      });
      if (!r?.success) throw new Error(r?.error || "Publication impossible.");
      window.dispatchEvent(new CustomEvent("viq-marketplace-changed", { detail: { dossier_id: Number(selectedId), publication_id: r.publication?.id || null } }));
      setMessage(r.updated ? "Publication mise à jour sur le réseau VehicleIQ." : "Dossier publié sur le réseau VehicleIQ.");
      setTimeout(() => onClose?.(), 450);
    } catch (e) { setMessage(e?.message || "Publication impossible."); }
    finally { setBusy(false); }
  };

  return (
    <div className="ws-overlay" role="presentation" onMouseDown={e => e.target === e.currentTarget && onClose?.()}>
      <section className="ws-modal network-publish-modal" role="dialog" aria-modal="true" aria-label="Publier un Dossier VIQ sur le réseau">
        <header className="ws-modal-head">
          <div><div className="ws-eyebrow">Réseau VehicleIQ</div><h2>Publier un Dossier VIQ</h2><p>Partagez une opportunité avec les autres professionnels sans exposer les coordonnées du client ni votre prix d'achat.</p></div>
          <button className="ws-icon-btn" onClick={onClose} aria-label="Fermer">✕</button>
        </header>
        <div className="network-publish-body">
          <label className="aff-field"><span className="aff-label">Dossier à publier</span>
            <select value={selectedId} onChange={e => { setSelectedId(e.target.value); setAskingPrice(""); }} disabled={loading || busy}>
              <option value="">{loading ? "Chargement…" : "— Sélectionner un Dossier VIQ —"}</option>
              {dossiers.map(d => <option key={d.id} value={d.id}>{[d.make, d.model, d.version, d.year].filter(Boolean).join(" ") || d.label || `Dossier #${d.id}`}{d.plaque ? ` · ${d.plaque}` : ""}</option>)}
            </select>
          </label>
          {selected && <div className="ws-note"><strong>{[selected.make, selected.model, selected.version].filter(Boolean).join(" ") || selected.label || `Dossier #${selected.id}`}</strong>{selected.km ? ` · ${Number(selected.km).toLocaleString("fr-FR")} km` : ""}{askingPrice ? ` · ${eur(askingPrice)} € demandé` : ""}</div>}
          <div className="network-publish-grid">
            <label className="aff-field"><span className="aff-label">Prix souhaité (€)</span><input type="number" min="0" step="100" value={askingPrice} onChange={e => setAskingPrice(e.target.value)} placeholder="Ex : 24 900" /></label>
            <label className="aff-field"><span className="aff-label">Note réseau</span><textarea value={note} onChange={e => setNote(e.target.value)} maxLength={1000} placeholder="État, disponibilité, conditions, éléments à signaler…" /></label>
          </div>
          <div className="ws-note">Visible sur le réseau : véhicule, caractéristiques, état/score disponible, localisation approximative, photos du dossier, prix souhaité et cette note. Les données client restent privées.</div>
          {message && <div className="ws-note" role="status">{message}</div>}
        </div>
        <div className="network-publish-actions"><button className="ws-btn secondary" onClick={onClose}>Annuler</button><button className="ws-btn primary" disabled={!selectedId || busy} onClick={publish}>{busy ? "Publication…" : "Publier sur le réseau"}</button></div>
      </section>
    </div>
  );
}
